/**
 * Onboarding Tour v6 — Interaktives Tutorial mit Spotlight-Effekt
 *
 * Features:
 * - Spotlight: Angesprochener Bereich wird aufgehellt, Rest abgedunkelt
 * - Auto-Navigation: Tour springt automatisch zur richtigen Seite
 * - Auto-Scroll: Scrollt zum hervorgehobenen Element
 * - Tooltip positioniert sich neben dem Spotlight-Bereich
 * - Keyboard-Navigation (Pfeiltasten, Enter)
 * - Demo-Modus: Tour ist ZWINGEND, kein Schließen möglich bis zum letzten Schritt
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronRight, ChevronLeft, Sparkles, RotateCcw } from "lucide-react";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useDemo } from "@/contexts/DemoContext";
import { useLocation } from "wouter";

interface TourStep {
  /** CSS selector for the target element */
  target: string;
  title: string;
  description: string;
  position: "top" | "bottom" | "left" | "right" | "center";
  /** Route to navigate to before showing this step (relative to /app) */
  route?: string;
  /** Only show if element exists */
  conditional?: boolean;
}

const ALL_TOUR_STEPS: TourStep[] = [
  // 1. Willkommen (Dashboard)
  {
    target: "[data-tour='greeting']",
    title: "Willkommen bei AYU!",
    description:
      "Das ist dein persönlicher Gesundheitsbereich. Hier siehst du deine Begrüßung — AYU passt sich deinem Tagesrhythmus an. Lass uns gemeinsam durch alle Funktionen gehen!",
    position: "bottom",
    route: "/app",
  },
  // 2. Telegram-Verknüpfung (Dashboard)
  {
    target: "[data-tour='telegram-card']",
    title: "Telegram verbinden",
    description:
      "Verbinde AYU mit Telegram, um tägliche Check-ins und Erinnerungen zu erhalten. Es gibt zwei Bots: Annam für persönliche Nachrichten und die AYU Community für Austausch.",
    position: "bottom",
    route: "/app",
  },
  // 3. Quick Actions (Dashboard)
  {
    target: "[data-tour='quick-actions']",
    title: "Schnellzugriff",
    description:
      "Deine wichtigsten Werkzeuge auf einen Blick: Das Tagebuch für tägliche Reflexion und Annam für persönliche Gespräche. Tippe einfach auf eine Karte, um loszulegen.",
    position: "bottom",
    route: "/app",
  },
  // 4. Statistiken (Dashboard)
  {
    target: "[data-tour='stats-row']",
    title: "Deine Statistiken & Streaks",
    description:
      "Hier siehst du auf einen Blick: Tagebuch-Einträge, AYU-Sitzungen, aktive Tage und deinen Streak. Dein Streak zählt die Tage, an denen du aktiv warst — wenn er in Gefahr ist, wirst du erinnert.",
    position: "top",
    route: "/app",
  },
  // 5. Gamification / AYU-Pfad (Dashboard)
  {
    target: "[data-tour='gamification']",
    title: "Dein AYU-Pfad — So funktioniert's!",
    description:
      "Prana-Punkte sind deine Energie-Währung: Du sammelst sie durch Tagebuch-Einträge (+10), Coach-Sitzungen (+15), Check-ins (+5) und Community-Beiträge (+5). Je mehr Punkte, desto höher dein Level — von Samen (Level 1) bis Erleuchtung (Level 7). Außerdem schaltest du Badges frei für besondere Meilensteine!",
    position: "top",
    route: "/app",
    conditional: true,
  },
  // 6. Sidebar — Tagebuch
  {
    target: "[data-tour='sidebar-tagebuch']",
    title: "Tagebuch",
    description:
      "Dein persönliches Gesundheitstagebuch. Halte hier deine Gedanken, Stimmung und Fortschritte fest. Nutze die Pfeile links und rechts neben dem Datum, um zwischen Tagen zu blättern, oder öffne den Verlauf für das Monatsarchiv.",
    position: "right",
    route: "/app",
  },
  // 7. Sidebar — Coach Sitzungen
  {
    target: "[data-tour='sidebar-coach-sitzungen']",
    title: "Coach Sitzungen",
    description:
      "Starte ein Gespräch mit Annam — per Text oder Sprache. Sie kennt deine Anamnese und begleitet dich individuell. Im Verlauf links siehst du alle bisherigen Sitzungen und kannst sie umbenennen oder weiterführen.",
    position: "right",
    route: "/app",
  },
  // 8. Sidebar — Sessions
  {
    target: "[data-tour='sidebar-sessions']",
    title: "Übungen & Sessions",
    description:
      "Geführte Übungen für Körper, Geist und Seele: Meditationen, Atemübungen, Body Scans und mehr. Basis-Sessions sind kostenlos, personalisierte Sessions werden mit deinem Paket freigeschaltet.",
    position: "right",
    route: "/app",
  },
  // 9. Sidebar — Kompass
  {
    target: "[data-tour='sidebar-kompass']",
    title: "Kompass",
    description:
      "Dein ayurvedischer Kompass zeigt dir personalisierte Empfehlungen für Ernährung, Routinen und Lebensstil — basierend auf deinem Phänotyp und deinen aktuellen Werten.",
    position: "right",
    route: "/app",
  },
  // 10. Sidebar — Community
  {
    target: "[data-tour='sidebar-community']",
    title: "Community",
    description:
      "Tausche dich mit anderen AYU-Nutzern aus. Teile Erfahrungen, stelle Fragen und finde Inspiration. Du kannst deine eigenen Beiträge jederzeit bearbeiten oder löschen.",
    position: "right",
    route: "/app",
  },
  // 11. Sidebar — Ernährung
  {
    target: "[data-tour='sidebar-ernährung']",
    title: "Ernährungsprofil",
    description:
      "Dein persönliches Ernährungsprofil basierend auf deinem ayurvedischen Phänotyp. Hier findest du Empfehlungen, welche Lebensmittel dir gut tun und welche du meiden solltest.",
    position: "right",
    route: "/app",
  },
  // 12. Sidebar — Tagesroutine
  {
    target: "[data-tour='sidebar-tagesroutine']",
    title: "Tagesroutine (Dinacharya)",
    description:
      "Dein individueller Tagesplan nach ayurvedischen Prinzipien: Morgen-, Mittags- und Abendroutine mit konkreten Zeitempfehlungen, Übungen und Kräuterempfehlungen.",
    position: "right",
    route: "/app",
  },
  // 13. Sidebar — Rezepte
  {
    target: "[data-tour='sidebar-rezepte']",
    title: "Rezepte",
    description:
      "Ayurvedische Rezepte, die zu deinem Dosha-Typ passen. Du kannst eigene Rezepte erstellen, Favoriten speichern und nach Mahlzeitentyp filtern.",
    position: "right",
    route: "/app",
  },
  // 14. Sidebar — Schlaf
  {
    target: "[data-tour='sidebar-schlaf']",
    title: "Schlaf-Tracker",
    description:
      "Erfasse und analysiere deine Schlafqualität. Trage Schlaf- und Aufwachzeiten ein, bewerte deine Erholung und erkenne Muster über die Zeit.",
    position: "right",
    route: "/app",
  },
  // 15. Sidebar — Fortschritt
  {
    target: "[data-tour='sidebar-fortschritt']",
    title: "Fortschritt",
    description:
      "Verfolge deine Gesundheitsreise: Streaks, Ziele und dein AYU-Score im Zeitverlauf. Setze dir eigene Ziele und sieh, wie sich deine Werte über Wochen und Monate verbessern.",
    position: "right",
    route: "/app",
  },
  // 16. Sidebar — Gamification
  {
    target: "[data-tour='sidebar-gamification']",
    title: "Gamification & AYU-Pfad",
    description:
      "Dein Spielfeld: Sammle Prana-Punkte, steige Level auf, verdiene Badges und nimm an Wochen-Challenges teil. Auf der Übersichtsseite findest du eine ausführliche Erklärung zu jedem Element.",
    position: "right",
    route: "/app",
  },
  // 17. Sidebar — Profil
  {
    target: "[data-tour='sidebar-profil']",
    title: "Profil & Einstellungen",
    description:
      "Hier verwaltest du dein Profil, Benachrichtigungen und Stimmenauswahl. Du kannst Benachrichtigungs-Uhrzeiten individuell einstellen und zwischen verschiedenen Kanälen (Telegram, Push, E-Mail) wählen.",
    position: "right",
    route: "/app",
  },
  // 18. Sidebar — Hilfe
  {
    target: "[data-tour='sidebar-hilfe-&-faq']",
    title: "Hilfe & FAQ",
    description:
      "Hast du Fragen? Der AYU Hilfe-Assistent beantwortet alles rund um die App, Ayurveda und deine Funktionen. Außerdem findest du hier häufig gestellte Fragen.",
    position: "right",
    route: "/app",
  },
  // 19. Sidebar — Feedback
  {
    target: "[data-tour='sidebar-feedback']",
    title: "Feedback",
    description:
      "Deine Meinung ist uns wichtig! Teile uns mit, was dir besonders gut gefällt (Freude), was verbessert werden könnte (Anmerkung) oder melde Fehlfunktionen. Dein Feedback hilft uns, AYU besser zu machen.",
    position: "right",
    route: "/app",
  },
  // 20. Abschluss
  {
    target: "[data-tour='greeting']",
    title: "Bereit für deinen Weg!",
    description:
      "Das war's! Du kennst jetzt alle Bereiche von AYU. Du kannst diese Tour jederzeit über dein Profil neu starten. Viel Freude mit AYU — dein persönlicher Gesundheitsbegleiter.",
    position: "bottom",
    route: "/app",
  },
];

const TOUR_STORAGE_KEY = "ayu_onboarding_tour_seen";
const TOUR_VERSION_KEY = "ayu_onboarding_tour_version";
const CURRENT_TOUR_VERSION = "7"; // v7: All app pages covered, Feedback, Sessions, Ernährung, Schlaf, etc.

export function OnboardingTour() {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const [availableSteps, setAvailableSteps] = useState<TourStep[]>([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { client } = useClientAuth();
  const { isDemo } = useDemo();
  const [, navigate] = useLocation();

  // In demo mode, the tour is MANDATORY — cannot be skipped
  const isForcedMode = isDemo;

  // Filter available steps
  useEffect(() => {
    if (!isActive) return;
    const steps = ALL_TOUR_STEPS.filter((step) => {
      if (!step.conditional) return true;
      return !!document.querySelector(step.target);
    });
    setAvailableSteps(steps);
  }, [isActive]);

  // Auto-start for new users OR always for demo
  useEffect(() => {
    // Demo mode: ALWAYS start the tour (ignore localStorage)
    if (isDemo) {
      const timer = setTimeout(() => {
        setIsActive(true);
        setCurrentStep(0);
      }, 800);
      return () => clearTimeout(timer);
    }

    // Normal mode: check if already seen
    const seen = localStorage.getItem(TOUR_STORAGE_KEY);
    const version = localStorage.getItem(TOUR_VERSION_KEY);

    if (seen && version === CURRENT_TOUR_VERSION) return;

    if (client?.onboardingComplete && seen) {
      localStorage.setItem(TOUR_VERSION_KEY, CURRENT_TOUR_VERSION);
      return;
    }

    const timer = setTimeout(() => {
      setIsActive(true);
      setCurrentStep(0);
    }, 1500);
    return () => clearTimeout(timer);
  }, [client, isDemo]);

  // Navigate to step's route and position spotlight
  const showStep = useCallback(
    (stepIndex: number) => {
      if (availableSteps.length === 0) return;
      const step = availableSteps[stepIndex];
      if (!step) return;

      const doPosition = () => {
        if (step.position === "center") {
          setSpotlightRect(null);
          setTooltipPos({
            top: window.innerHeight / 2 - 120,
            left: Math.max(12, window.innerWidth / 2 - 170),
          });
          setIsNavigating(false);
          return;
        }

        const el = document.querySelector(step.target) as HTMLElement | null;
        if (!el) {
          // Element not found, center the tooltip
          setSpotlightRect(null);
          setTooltipPos({
            top: window.innerHeight / 2 - 120,
            left: Math.max(12, window.innerWidth / 2 - 170),
          });
          setIsNavigating(false);
          return;
        }

        // Scroll element into view first
        const rect = el.getBoundingClientRect();
        const isInView =
          rect.top >= 0 && rect.bottom <= window.innerHeight;

        if (!isInView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          // Wait for scroll to finish, then position
          setTimeout(() => {
            const newRect = el.getBoundingClientRect();
            positionSpotlightAndTooltip(newRect, step.position);
            setIsNavigating(false);
          }, 500);
        } else {
          positionSpotlightAndTooltip(rect, step.position);
          setIsNavigating(false);
        }
      };

      // Check if we need to navigate to a different route
      if (step.route) {
        const currentPath = window.location.pathname;
        const targetPath = step.route;
        if (currentPath !== targetPath) {
          setIsNavigating(true);
          navigate(targetPath);
          // Wait for page to render
          setTimeout(doPosition, 600);
          return;
        }
      }

      doPosition();
    },
    [availableSteps, navigate]
  );

  const positionSpotlightAndTooltip = useCallback(
    (rect: DOMRect, position: string) => {
      // Set spotlight with padding
      const padding = 12;
      const spotRect = new DOMRect(
        rect.left - padding,
        rect.top - padding,
        rect.width + padding * 2,
        rect.height + padding * 2
      );
      setSpotlightRect(spotRect);

      // Responsive tooltip dimensions
      const tooltipWidth = Math.min(340, window.innerWidth - 24);
      // Use actual measured height if available, else conservative estimate
      const measuredHeight = tooltipRef.current?.offsetHeight ?? 0;
      const tooltipHeight = measuredHeight > 0 ? measuredHeight + 16 : 320;
      const margin = 16;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // On small screens, always center at bottom to guarantee button visibility
      if (vw < 500 || vh < 560) {
        const w = tooltipWidth;
        const safeTop = Math.max(60, vh - tooltipHeight - 16);
        setTooltipPos({
          top: safeTop,
          left: Math.max(12, (vw - w) / 2),
        });
        return;
      }

      let top = 0;
      let left = 0;

      switch (position) {
        case "bottom":
          top = rect.bottom + margin;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case "top":
          top = rect.top - tooltipHeight - margin;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case "left":
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - margin;
          break;
        case "right": {
          const spaceRight = vw - rect.right;
          if (spaceRight < tooltipWidth + margin) {
            // Not enough room on right → fall back to bottom-center
            top = rect.bottom + margin;
            left = vw / 2 - tooltipWidth / 2;
          } else {
            top = rect.top + rect.height / 2 - tooltipHeight / 2;
            left = rect.right + margin;
          }
          break;
        }
      }

      // Clamp within viewport with generous bottom safety margin
      left = Math.max(12, Math.min(left, vw - tooltipWidth - 12));
      top = Math.max(12, Math.min(top, vh - tooltipHeight - 16));

      setTooltipPos({ top, left });
    },
    []
  );

  // Trigger showStep
  useEffect(() => {
    if (!isActive || availableSteps.length === 0) return;
    const timer = setTimeout(() => showStep(currentStep), 150);
    return () => clearTimeout(timer);
  }, [isActive, currentStep, availableSteps, showStep]);

  // Reposition on resize/scroll
  useEffect(() => {
    if (!isActive) return;
    const handleReposition = () => showStep(currentStep);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isActive, currentStep, showStep]);

  // After tooltip renders, clamp its top so it never exceeds the viewport bottom
  useEffect(() => {
    if (!tooltipRef.current || !isActive) return;
    const h = tooltipRef.current.offsetHeight;
    const maxTop = window.innerHeight - h - 16;
    if (tooltipPos.top > maxTop) {
      setTooltipPos((prev) => ({ ...prev, top: Math.max(12, maxTop) }));
    }
  }, [tooltipPos.left, isActive]); // re-run when step changes (left changes each step)

  const closeTour = useCallback(() => {
    // In forced mode (demo), only allow closing on the last step
    if (isForcedMode && currentStep < availableSteps.length - 1) {
      return; // Cannot close during demo
    }
    setIsActive(false);
    setSpotlightRect(null);
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
    localStorage.setItem(TOUR_VERSION_KEY, CURRENT_TOUR_VERSION);
  }, [isForcedMode, currentStep, availableSteps.length]);

  const nextStep = useCallback(() => {
    if (currentStep < availableSteps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      closeTour();
    }
  }, [currentStep, availableSteps.length, closeTour]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;
    const handleKey = (e: KeyboardEvent) => {
      // In forced mode, Escape does nothing
      if (e.key === "Escape" && !isForcedMode) closeTour();
      if (e.key === "ArrowRight" || e.key === "Enter") nextStep();
      if (e.key === "ArrowLeft") prevStep();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isActive, closeTour, nextStep, prevStep, isForcedMode]);

  if (!isActive || availableSteps.length === 0) return null;

  const step = availableSteps[currentStep];
  const isLast = currentStep === availableSteps.length - 1;
  const progress = ((currentStep + 1) / availableSteps.length) * 100;

  return (
    <>
      {/* Full-screen overlay with spotlight cutout using SVG mask */}
      <svg
        className="fixed inset-0 z-[9998] pointer-events-none"
        style={{ width: "100vw", height: "100vh" }}
      >
        <defs>
          <mask id="spotlight-mask">
            {/* White = visible (dark overlay), Black = hidden (spotlight hole) */}
            <rect width="100%" height="100%" fill="white" />
            {spotlightRect && (
              <rect
                x={spotlightRect.left}
                y={spotlightRect.top}
                width={spotlightRect.width}
                height={spotlightRect.height}
                rx="16"
                ry="16"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill={isForcedMode ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.55)"}
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Clickable overlay — in forced mode, clicking outside does nothing */}
      <div
        className="fixed inset-0 z-[9998]"
        onClick={isForcedMode ? undefined : closeTour}
        style={{ background: "transparent", cursor: isForcedMode ? "not-allowed" : "pointer" }}
      />

      {/* Spotlight border glow */}
      {spotlightRect && (
        <div
          className="fixed z-[9999] pointer-events-none rounded-2xl transition-all duration-500 ease-out"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            border: "2px solid rgba(201,169,110,0.5)",
            boxShadow:
              "0 0 20px rgba(201,169,110,0.3), inset 0 0 20px rgba(201,169,110,0.1)",
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`fixed z-[10000] flex flex-col bg-white rounded-2xl shadow-2xl border border-[#C9A96E]/20 overflow-hidden transition-all duration-500 ease-out ${
          isNavigating ? "opacity-0 scale-95" : "opacity-100 scale-100"
        }`}
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          width: Math.min(340, window.innerWidth - 24),
          maxHeight: Math.max(240, window.innerHeight - tooltipPos.top - 16),
          pointerEvents: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress bar */}
        <div className="h-1 bg-[#f0f3eb] shrink-0">
          <div
            className="h-full bg-gradient-to-r from-[#6b7f4e] to-[#C9A96E] transition-all duration-500 ease-out rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Demo badge */}
        {isForcedMode && (
          <div className="bg-[#C9A96E]/10 px-5 py-1.5 text-center shrink-0">
            <span className="text-[10px] font-medium text-[#8b7a5e]">
              Demo-Tour — Bitte durchlaufe alle Schritte
            </span>
          </div>
        )}

        {/* Header */}
        <div className="bg-gradient-to-r from-[#6b7f4e]/5 to-[#C9A96E]/5 px-5 pt-4 pb-3 flex items-start justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#6b7f4e]/20 to-[#C9A96E]/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#6b7f4e]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#2d3a1e]">{step.title}</h3>
              <p className="text-[10px] text-[#8a9680]">
                Schritt {currentStep + 1} von {availableSteps.length}
              </p>
            </div>
          </div>
          {/* X button: only shown in non-forced mode */}
          {!isForcedMode && (
            <button
              onClick={closeTour}
              className="text-[#2D2D2D]/30 hover:text-[#2D2D2D]/60 transition-colors p-1 rounded-lg hover:bg-black/5"
              aria-label="Tour schließen"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Body — scrollable so long descriptions never push footer off screen */}
        <div className="px-5 py-3 overflow-y-auto flex-1 min-h-0">
          <p className="text-[13px] text-[#5a6b4a] leading-relaxed">
            {step.description}
          </p>
        </div>

        {/* Footer — always pinned, never scrolled away */}
        <div className="px-5 pb-4 pt-2 flex items-center justify-between shrink-0 border-t border-[#f0f3eb]">
          {/* Step counter instead of dots (20 dots overflow on small screens) */}
          <span className="text-[11px] text-[#8a9680] tabular-nums">
            {currentStep + 1} / {availableSteps.length}
          </span>

          {/* Navigation buttons */}
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={prevStep}
                className="flex items-center gap-1 text-xs text-[#5a6b4a] hover:text-[#2d3a1e] transition-colors px-2 py-1.5 rounded-lg hover:bg-[#f0f3eb]"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Zurück
              </button>
            )}
            <button
              onClick={nextStep}
              className="flex items-center gap-1 text-xs font-medium text-white bg-[#6b7f4e] hover:bg-[#5a6b3e] transition-colors px-4 py-2 rounded-xl shadow-sm"
            >
              {isLast ? (isForcedMode ? "Los geht's!" : "Fertig!") : "Weiter"}
              {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Reset-Funktion: Tour erneut anzeigen (z.B. aus Profil-Einstellungen)
 */
export function resetOnboardingTour() {
  localStorage.removeItem(TOUR_STORAGE_KEY);
  localStorage.removeItem(TOUR_VERSION_KEY);
}

/**
 * Button-Komponente zum Neustarten der Tour (für Profil-Seite)
 */
export function RestartTourButton() {
  const handleRestart = () => {
    resetOnboardingTour();
    window.location.href = "/app";
  };

  return (
    <button
      onClick={handleRestart}
      className="flex items-center gap-2 text-sm text-[#6b7f4e] hover:text-[#5a6b3e] transition-colors px-3 py-2 rounded-xl hover:bg-[#6b7f4e]/10"
    >
      <RotateCcw className="w-4 h-4" />
      Tutorial neu starten
    </button>
  );
}
